import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { CheckCircle, Award, Target, Phone, ArrowRight } from "lucide-react";
import Layout from "@/components/Layout";
import { useLanguage } from "@/contexts/LanguageContext";
import maintenanceImage from "@/assets/maintenance.jpg";
import heroImage from "@/assets/hero-elevator.jpg";

const About = () => {
  const { language, t } = useLanguage();

  const copy =
    language === "en"
      ? {
          heroTitle: "About Us",
          heroSubtitle: "Almost 90 years of experience in the elevator industry.",
          heroAlt: "LIFTCO elevators",
          companyHeading: "LIFTCO",
          companySub: "E.E.",
          paragraphs: [
            "LIFTCO E.E., based in Thessaloniki, has been active in the elevator sector since 1935, focusing on three main areas: installation, repair and maintenance of elevators and lifting systems for people and goods.",
            "With strong turnover and a team of 10 employees, the company holds one of the leading positions in the elevator sector in Thessaloniki and Northern Greece.",
            "Today’s demands require increasingly advanced solutions for passenger and freight mobility.",
          ],
          techAlt: "LIFTCO technician",
          philosophyTitle: "Consistency & Professionalism",
          philosophyText: [
            "Our philosophy for the products and services we offer is built around meeting the highest requirements in the elevator field.",
            "To achieve excellent quality, we use advanced technologies and innovative solutions, combined with continuous high-standard quality control (DIN ISO 9001/1992).",
            "Our products are available not only in standardized constructions but also in specialized mechanical and architectural solutions when needed, ensuring reliable mobility for passengers and freight.",
            "This is made possible through our specialized experience and continuous R&D in high-performance products, including safety components, control panels with specialized programming and support systems.",
          ],
          goalTitle: "Our Goal",
          goalText: [
            "These are some of the reasons why LIFTCO E.E. is considered one of the pioneering specialized consultants and partners for our customers.",
            "With the extensive experience of our team and the quality of the elevators we install, our goal is customer satisfaction—covering needs from initial design and installation to timely support during operation, ensuring long-term value of the initial investment.",
            "We provide the advantages of working with a larger company while maintaining a direct, personal approach to solve issues quickly.",
          ],
          whyTitle: "Why Choose Us",
          whyItems: [
            "Long-standing experience since 1935",
            "DIN ISO 9001/1992 certification",
            "Specialized personnel",
            "Modern technological equipment",
            "24/7 service",
            "Competitive pricing",
            "Fast response",
            "Service across Northern Greece",
          ],
          ctaTitle: "Get in Touch",
          ctaText:
            "Let’s discuss your needs and find the ideal solutions together. We serve Thessaloniki and all of Northern Greece.",
        }
      : {
          heroTitle: "Η Εταιρεία Μας",
          heroSubtitle: "Σχεδόν 90 χρόνια εμπειρίας στον τομέα των ανελκυστήρων.",
          heroAlt: "LIFTCO ανελκυστήρες",
          companyHeading: "LIFTCO",
          companySub: "Ε.Ε.",
          paragraphs: [
            "Η τεχνική εταιρεία ανελκυστήρων LIFTCO Ε.Ε. η οποία βρίσκεται στην Θεσσαλονίκη, δραστηριοποιείται στο χώρο των ανελκυστήρων από το 1935 με πολύ υψηλά επίπεδα παραγωγικότητας σε τρεις κύριους τομείς: εγκατάσταση, επισκευή και συντήρηση ανελκυστήρων και πάσης φύσεως ανυψωτικού προσώπων και εμπορευμάτων.",
            "Με μεγάλο κύκλο εργασιών και 10 εργαζομένους, κατέχει μία από τις καλύτερες θέσεις στον τομέα των ανελκυστήρων στην Θεσσαλονίκη και στην Βόρεια Ελλάδα.",
            "Η ανάγκη κάλυψης των σύγχρονων απαιτήσεων επιβάλλει όλο και πιο σύνθετες λύσεις στη μετακίνηση επιβατών και φορτίων.",
          ],
          techAlt: "LIFTCO τεχνικός",
          philosophyTitle: "Συνέπεια & Επαγγελματισμός",
          philosophyText: [
            "Η φιλοσοφία μας για τα προϊόντα και τις υπηρεσίες που προσφέρουμε, κινείται στη κάλυψη των υψηλότερων απαιτήσεων στο χώρο των ανελκυστήρων.",
            "Για την επίτευξη της άριστης ποιότητας των εξαρτημάτων και συστημάτων γίνεται χρήση των πιο προηγμένων τεχνολογιών και καινοτόμων λύσεων - σε συνδυασμό με το συνεχή και υψηλών προδιαγραφών ποιοτικό έλεγχο (DIN ISO 9001/1992).",
            "Τα προϊόντα μας διατίθενται όχι μόνο σε τυποποιημένες κατασκευές αλλά και όπου απαιτούνται εξειδικευμένες μηχανολογικές και αρχιτεκτονικές κατασκευές για να εξασφαλίσουν αξιόπιστη μετακίνηση επιβατών και φορτίων.",
            "Αυτό γίνεται εφικτό με την εξειδικευμένη εμπειρία που διαθέτουμε χάρη στη συνεχή έρευνα και ανάπτυξη προϊόντων υψηλής απόδοσης που αφορούν ηλεκτρικά και μηχανικά εξαρτήματα ασφαλείας, ηλεκτρονικούς πίνακες χειρισμών με εξειδικευμένο προγραμματισμό και συστήματα υποστήριξης.",
          ],
          goalTitle: "Ο Στόχος Μας",
          goalText: [
            "Αυτοί είναι κάποιοι από τους λόγους που κατατάσσουν την LIFTCO Ε.Ε. ως μία από τις πρωτοπόρες εταιρείες ειδικευμένων συμβούλων και συνεργατών για τους πελάτες μας.",
            "Με υπόβαθρο την πολυετή εμπειρία των στελεχών μας και την ποιότητα των ανελκυστήρων που εγκαθιστούμε έχουμε σαν στόχο την ικανοποίηση του πελάτη με σκοπό την πλήρη κάλυψη των αναγκών του από τον αρχικό σχεδιασμό και την εγκατάσταση ως την έγκυρη και έγκαιρη εξυπηρέτηση κατά τη διάρκεια λειτουργίας τους, για τη μακροχρόνια διατήρηση της αξίας της αρχικής επένδυσης.",
            "Η εταιρεία μας παρέχει τα πλεονεκτήματα της συνεργασίας με μια μεγάλη εταιρεία αλλά ταυτόχρονα διατηρεί την άμεση προσέγγιση και προσωπική σχέση με τον πελάτη για την επίλυση των θεμάτων.",
          ],
          whyTitle: "Γιατί να μας Επιλέξετε",
          whyItems: [
            "Πολυετής εμπειρία από το 1935",
            "Πιστοποίηση DIN ISO 9001/1992",
            "Εξειδικευμένο ανθρώπινο δυναμικό",
            "Σύγχρονος τεχνολογικός εξοπλισμός",
            "24ωρη εξυπηρέτηση",
            "Ανταγωνιστικές τιμές",
            "Άμεση ανταπόκριση",
            "Εξυπηρέτηση σε όλη τη Β. Ελλάδα",
          ],
          ctaTitle: "Επικοινωνήστε Μαζί Μας",
          ctaText:
            "Για να συζητήσουμε τις ανάγκες σας και μαζί να βρούμε τις ιδανικές λύσεις. Εξυπηρετούμε άμεσα την Θεσσαλονίκη και όλη την Βόρεια Ελλάδα.",
        };

  return (
    <Layout>
      {/* Hero Section */}
      <section className="relative py-24 md:py-32">
        <div className="absolute inset-0">
          <img src={heroImage} alt={copy.heroAlt} className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-hero-gradient" />
        </div>
        <div className="container relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="max-w-3xl"
          >
            <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-bold text-primary-foreground mb-6">
              {copy.heroTitle}
            </h1>
            <p className="text-lg md:text-xl text-primary-foreground/80">{copy.heroSubtitle}</p>
          </motion.div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-20">
        <div className="container">
          <div className="grid lg:grid-cols-2 gap-16 items-start">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <h2 className="font-heading text-3xl font-bold text-foreground mb-6">
                {copy.companyHeading} <span className="text-primary">{copy.companySub}</span>
              </h2>
              <div className="prose prose-lg max-w-none text-muted-foreground space-y-6">
                {copy.paragraphs.map((p) => (
                  <p key={p}>{p}</p>
                ))}
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <div className="aspect-[4/3] rounded-2xl overflow-hidden shadow-lg">
                <img src={maintenanceImage} alt={copy.techAlt} className="w-full h-full object-cover" />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Philosophy Section */}
      <section className="py-20 bg-muted/30">
        <div className="container">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="max-w-4xl mx-auto"
          >
            <div className="flex items-center gap-4 mb-6">
              <Award className="h-10 w-10 text-accent" />
              <h2 className="font-heading text-3xl font-bold text-foreground">{copy.philosophyTitle}</h2>
            </div>
            <div className="space-y-6 text-muted-foreground">
              {copy.philosophyText.map((p) => (
                <p key={p}>{p}</p>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-20">
        <div className="container">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="max-w-4xl mx-auto"
          >
            <div className="flex items-center gap-4 mb-6">
              <Target className="h-10 w-10 text-primary" />
              <h2 className="font-heading text-3xl font-bold text-foreground">{copy.goalTitle}</h2>
            </div>
            <div className="space-y-6 text-muted-foreground">
              {copy.goalText.map((p) => (
                <p key={p}>{p}</p>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-20 bg-muted/30">
        <div className="container">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-foreground mb-4">
              {copy.whyTitle}
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            {copy.whyItems.map((item, index) => (
              <motion.div
                key={item}
                initial={{ opacity: 0, x: index % 2 === 0 ? -20 : 20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: index * 0.05 }}
                className="flex items-center gap-4 bg-card p-4 rounded-lg shadow-sm"
              >
                <CheckCircle className="h-6 w-6 text-accent flex-shrink-0" />
                <span className="text-foreground font-medium">{item}</span>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-primary">
        <div className="container text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-primary-foreground mb-4">
              {copy.ctaTitle}
            </h2>
            <p className="text-primary-foreground/80 max-w-2xl mx-auto mb-8">{copy.ctaText}</p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button asChild variant="hero" size="lg">
                <a href="tel:2310270290">
                  <Phone className="h-5 w-5" />
                  2310 270290
                </a>
              </Button>
              <Button asChild variant="heroOutline" size="lg">
                <Link to="/contact">
                  {t("common.contactForm")}
                  <ArrowRight className="h-5 w-5" />
                </Link>
              </Button>
            </div>
          </motion.div>
        </div>
      </section>
    </Layout>
  );
};

export default About;
